import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowClockwise,
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  Copy,
  FilmSlate,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { friendlyError, toastError } from "@/lib/userMessages";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { RoteiroScriptView } from "@/components/shared/RoteiroScriptView";
import { useRoteiroHistory } from "@/features/roteiros/hooks/useRoteiroHistory";
import { useDeleteRoteiro } from "@/features/roteiros/hooks/useDeleteRoteiro";
import { useRenameRoteiro } from "@/features/roteiros/hooks/useRenameRoteiro";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useGenerateRoteiro } from "@/features/roteiros/hooks/useGenerateRoteiro";
import { cn } from "@/lib/utils";
import {
  buildRoteiroReferenceText,
  normalizeObjetivoLabel,
  normalizeObjetivoValue,
  normalizeTypeLabel,
  normalizeTypeValue,
  parseRoteiroPayload,
} from "@/features/roteiros/parseRoteiro";
import { getRoteiroFamilyJobs, getRoteiroRootJobId } from "@/features/roteiros/historyGrouping";

export default function RoteiroDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const history = useRoteiroHistory();
  const deleteRoteiro = useDeleteRoteiro();
  const renameRoteiro = useRenameRoteiro();
  const generateVariation = useGenerateRoteiro();
  const [variationContext, setVariationContext] = useState("");

  const job = history.data?.find((j) => j.id === id) ?? null;
  const result = Array.isArray(job?.ai_job_results)
    ? job?.ai_job_results[0]
    : job?.ai_job_results;
  const script = result?.analysis_json?.script ?? null;
  const topic = result?.analysis_json?.topic ?? null;
  const parsed = script ? parseRoteiroPayload(script) : null;
  const persisted = job?.request_metadata?.normalized ?? null;
  const typeLabel =
    normalizeTypeLabel(persisted?.type) ??
    parsed?.typeLabel ??
    normalizeTypeLabel(result?.analysis_json?.type) ??
    null;
  const objetivoLabel =
    normalizeObjetivoLabel(persisted?.objective) ??
    parsed?.objetivoLabel ??
    normalizeObjetivoLabel(result?.analysis_json?.objective) ??
    null;
  const typeValue =
    normalizeTypeValue(persisted?.type) ??
    parsed?.typeValue ??
    normalizeTypeValue(result?.analysis_json?.type) ??
    null;
  const objetivoValue =
    normalizeObjetivoValue(persisted?.objective) ??
    parsed?.objetivoValue ??
    normalizeObjetivoValue(result?.analysis_json?.objective) ??
    null;
  const rootJobId = job ? getRoteiroRootJobId(job) : null;
  const displayTitle = (topic ?? `Roteiro ${job?.id.slice(0, 8) ?? ""}`)
    .replace(/^v\d+\s+/i, "")
    .trim();

  const familyJobs = useMemo(() => {
    if (!history.data || !job) return job ? [job] : [];
    return getRoteiroFamilyJobs(history.data, job);
  }, [history.data, job]);

  const statusLabel =
    job?.status === "done"
      ? "Concluído"
      : job?.status === "failed"
        ? "Falhou"
        : job?.status === "pending" || job?.status === "running"
          ? "Processando"
          : job?.status ?? "";

  const currentVersionNumber = familyJobs.findIndex((item) => item.id === job?.id) + 1;

  usePageTitle(displayTitle || "Roteiro");

  function handleCopy() {
    if (!script) return;
    void navigator.clipboard.writeText(script).then(() => {
      toast.success("Roteiro copiado!");
    });
  }

  function handleGenerateVariation() {
    if (!topic || !typeValue || !objetivoValue || !job) {
      toast.error("Faltam metadados deste roteiro para gerar uma variação.");
      return;
    }

    const previousVersions = familyJobs
      .filter((item) => item.id !== job.id)
      .slice(-4)
      .map((item, index) => {
        const itemResult = Array.isArray(item.ai_job_results) ? item.ai_job_results[0] : item.ai_job_results;
        const itemScript = itemResult?.analysis_json?.script;
        if (typeof itemScript !== "string") return null;
        return `Versão ${index + 1} já existente:\n${buildRoteiroReferenceText(itemScript)}`;
      })
      .filter(Boolean);

    const details = [
      variationContext.trim()
        ? `Contexto adicional do usuário para a nova variação:\n${variationContext.trim()}`
        : "Crie uma nova variação mantendo o mesmo tema, objetivo e formato.",
      "Use as versões anteriores apenas como contexto de referência. Não repita hook, exemplos, CTA, framing ou estrutura de forma parecida.",
      previousVersions.length > 0 ? `Versões anteriores:\n\n${previousVersions.join("\n\n")}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");

    generateVariation.mutate(
      {
        topic: displayTitle || topic,
        type: typeValue,
        objetivo: objetivoValue,
        details,
        generationKind: "variant",
        parentJobId: job.id,
        rootJobId: rootJobId ?? job.id,
      },
      {
        onSuccess: (data) => {
          setVariationContext("");
          toast.success("Nova variação gerada.");
          if (data.result.jobId) navigate(`/roteiros/${data.result.jobId}`);
        },
        onError: (err) => {
          toastError(err, "Não foi possível gerar nova variação. Tente novamente.");
        },
      },
    );
  }

  return (
    <AppLayout>
      <div className="space-y-5 animate-fade-in w-full">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/roteiros")}
          className="gap-1.5"
        >
          <ArrowLeft weight="bold" className="w-4 h-4" />
          Voltar
        </Button>

        {(history.isPending || (history.isFetching && !job)) && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {history.isError && (
          <div className="bg-destructive/15 border border-destructive/30 rounded-lg p-4 flex items-start gap-3">
            <Warning weight="duotone" className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">Erro ao carregar</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {friendlyError(history.error, "Não foi possível carregar o roteiro. Tente atualizar a página.")}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => void history.refetch()}>
              <ArrowClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5" />
              Tentar novamente
            </Button>
          </div>
        )}

        {!history.isPending && !history.isFetching && !history.isError && !job && (
          <div className="text-center py-12">
            <p className="text-sm font-medium text-foreground">Roteiro não encontrado</p>
          </div>
        )}

        {job && (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
                  <FilmSlate weight="duotone" className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-foreground min-w-0">
                    <InlineEditText
                      value={displayTitle}
                      onSave={async (name) => {
                        if (!job.id) return;
                        try {
                          await renameRoteiro.mutateAsync({ id: job.id, topic: name });
                          toast.success("Renomeado com sucesso.");
                        } catch (err) {
                          toastError(err, "Não foi possível renomear. Tente novamente.");
                          throw err;
                        }
                      }}
                      placeholder="Roteiro sem título"
                    />
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(job.created_at).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  {typeLabel || objetivoLabel || currentVersionNumber > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {currentVersionNumber > 0 ? (
                        <Badge variant="outline" className="text-[11px]">
                          Versão {currentVersionNumber}
                          {familyJobs.length > 1 ? ` de ${familyJobs.length}` : ""}
                        </Badge>
                      ) : null}
                      {typeLabel ? (
                        <Badge variant="secondary" className="text-[11px]">
                          {typeLabel}
                        </Badge>
                      ) : null}
                      {objetivoLabel ? (
                        <Badge variant="outline" className="text-[11px]">
                          {objetivoLabel}
                        </Badge>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {job.id && (
                  <DeleteEntityButton
                    entityLabel="roteiro"
                    confirmDescription="O roteiro será removido do seu histórico permanentemente."
                    onConfirm={async () => {
                      try {
                        await deleteRoteiro.mutateAsync(job.id);
                        toast.success("Roteiro excluído.");
                        navigate("/roteiros");
                      } catch (err) {
                        toastError(err, "Não foi possível excluir o roteiro. Tente novamente.");
                        throw err;
                      }
                    }}
                    data-testid="roteiro-detail-delete"
                  />
                )}
                {job.status === "done" ? (
                  <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Concluído
                  </Badge>
                ) : job.status === "failed" ? (
                  <Badge variant="destructive" className="gap-1">
                    <Warning className="w-3 h-3" />
                    Falhou
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1">
                    <CircleNotch className="w-3 h-3 animate-spin" />
                    {statusLabel}
                  </Badge>
                )}
              </div>
            </div>

            {script ? (
              <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopy}
                      className="gap-1.5 hover:bg-primary/10 hover:text-primary"
                    >
                      <Copy weight="regular" className="w-4 h-4" />
                      Copiar roteiro
                    </Button>
                  </div>
                  <RoteiroScriptView script={script} />
                </div>

                <aside className="space-y-4">
                  <section className="rounded-xl border border-border/50 bg-card p-4 space-y-3">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground">Nova variação</h2>
                      <p className="text-xs text-muted-foreground mt-1">
                        Mantém tema, formato e objetivo. Você só adiciona o novo contexto.
                      </p>
                    </div>
                    <Textarea
                      value={variationContext}
                      onChange={(event) => setVariationContext(event.target.value)}
                      placeholder="Ex: evita repetir o hook anterior, puxa mais para prova social, deixa menos técnico, responde a objeção X."
                      className="min-h-[140px] resize-y"
                      disabled={generateVariation.isPending}
                    />
                    <Button
                      className="w-full gap-2"
                      onClick={handleGenerateVariation}
                      disabled={generateVariation.isPending || !typeValue || !objetivoValue}
                    >
                      {generateVariation.isPending ? (
                        <>
                          <CircleNotch className="w-4 h-4 animate-spin" />
                          Gerando variação...
                        </>
                      ) : (
                        <>
                          <ArrowsClockwise className="w-4 h-4" />
                          Gerar nova variação
                        </>
                      )}
                    </Button>
                    {!typeValue || !objetivoValue ? (
                      <p className="text-xs text-amber-400">
                        Este roteiro ainda não tem metadados completos para regeneração guiada.
                      </p>
                    ) : null}
                  </section>

                  <section className="rounded-xl border border-border/50 bg-card p-4 space-y-3">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground">Versões deste roteiro</h2>
                      <p className="text-xs text-muted-foreground mt-1">
                        Histórico agrupado para comparar sem abrir vários fluxos do zero.
                      </p>
                    </div>
                    <div className="space-y-2">
                      {familyJobs.map((item, index) => {
                        const itemResult = Array.isArray(item.ai_job_results)
                          ? item.ai_job_results[0]
                          : item.ai_job_results;
                        const itemTopic = itemResult?.analysis_json?.topic ?? `Roteiro ${item.id.slice(0, 8)}`;
                        const isCurrent = item.id === job.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => navigate(`/roteiros/${item.id}`)}
                            className={cn(
                              "w-full rounded-lg border p-3 text-left transition-colors",
                              isCurrent
                                ? "border-primary/40 bg-primary/5"
                                : "border-border/40 bg-secondary/10 hover:border-primary/30 hover:bg-secondary/20",
                            )}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Versão {index + 1}
                              </span>
                              {isCurrent ? <Badge variant="secondary">Atual</Badge> : null}
                            </div>
                            <p className="mt-2 text-sm font-medium text-foreground line-clamp-2">
                              {itemTopic.replace(/^v\d+\s+/i, "")}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {new Date(item.created_at).toLocaleString("pt-BR")}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </aside>
              </div>
            ) : job.status === "failed" ? (
              <div className="bg-destructive/15 border border-destructive/30 rounded-lg p-4">
                <p className="text-sm text-destructive font-medium">Geração falhou</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Volte para a lista e gere um novo roteiro.
                </p>
              </div>
            ) : (
              <div className="bg-card border border-border/50 rounded-xl p-6 text-center">
                <CircleNotch className="w-6 h-6 animate-spin mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Processando…</p>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
